import { useState, useEffect, useRef } from 'react';
import { Heart, User, Mail, MapPin, Calendar, Book, Music, Church, Coffee, Image, Save, Loader, MessageCircle, LogOut, Search, DollarSign, FileText, Award, Briefcase, GraduationCap, Clock, GitBranch, Users, Smile, Star } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

// Initialize Firebase with your credentials
const firebaseConfig = {
apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
appId: process.env.REACT_APP_FIREBASE_APP_ID,
measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase services
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Cloudinary configuration
const cloudinaryConfig = {
cloudName: "dryrxwnej",
uploadPreset: "chat-uploads",
apiUrl: 'https://api.cloudinary.com/v1_1/dryrxwnej/image/upload'
};

export default function Profile() {
const [activeTab, setActiveTab] = useState('profile');
const [loading, setLoading] = useState(false);
const [saved, setSaved] = useState(false);
const [userId, setUserId] = useState(null);
const [loadingProfile, setLoadingProfile] = useState(true);
const [userInfo, setUserInfo] = useState(null);
const [uploadProgress, setUploadProgress] = useState(0);
const fileInputRef = useRef(null);
const [imagePreview, setImagePreview] = useState(null);
const navigate = useNavigate();
const [unreadMessages, setUnreadMessages] = useState(0);

// Form validation states
const [formErrors, setFormErrors] = useState({});
const [attemptedSubmit, setAttemptedSubmit] = useState(false);

// Extended form data with new fields
const [formData, setFormData] = useState({
firstName: '',
lastName: '',
age: '',
gender: '',
height: '',
hairColor: '',
eyeColor: '',
ethnicity: '',
city: '',
country: '',
zipCode: '',
denomination: '',
churchInvolvement: '',
aboutMe: '',
personalTestimony: '',
faithJourney: '',
spiritualMaturity: '',
favoriteBibleVerse: '',
importantDoctrine: '',
ministryInterests: [],
hobbies: [],
lookingFor: '',
dealBreakers: '',
annualIncome: '',
educationLevel: '',
occupation: '',
relationshipGoals: '',
familyPlans: '',
photoUrl: '',
attendanceFrequency: '',
prayerFrequency: '',
bibleReadingFrequency: '',
conversionAge: '',
missionTripExperience: '',
marriageViews: '',
serviceActivities: [],
languagesSpoken: []
});

// Dropdown options
const ethnicityOptions = [
"African American/Black",
"African",
"Asian",
"Caucasian/White",
"Mixed-Race",
"Hispanic/Latino",
"Native American",
"Pacific Islander",
"Middle Eastern",
"Multiracial",
"Other"
];

// Height options in feet and inches
const heightOptions = [
"4'8\" (142 cm)",
"4'9\" (145 cm)",
"4'10\" (147 cm)",
"4'11\" (150 cm)",
"5'0\" (152 cm)",
"5'1\" (155 cm)",
"5'2\" (157 cm)",
"5'3\" (160 cm)",
"5'4\" (163 cm)",
"5'5\" (165 cm)",
"5'6\" (168 cm)",
"5'7\" (170 cm)",
"5'8\" (173 cm)",
"5'9\" (175 cm)",
"5'10\" (178 cm)",
"5'11\" (180 cm)",
"6'0\" (183 cm)",
"6'1\" (185 cm)",
"6'2\" (188 cm)",
"6'3\" (191 cm)",
"6'4\" (193 cm)",
"6'5\" (196 cm)",
"6'6\" (198 cm)",
"6'7\" (201 cm)",
"6'8\" (203 cm)",
"6'9\" (206 cm)",
"6'10\" (208 cm)",
"6'11\" (211 cm)",
"7'0\" (213 cm)"
];

// Detailed hair color options
const hairColorOptions = [
"Black",
"Dark Brown",
"Medium Brown",
"Light Brown",
"Dark Blonde",
"Medium Blonde",
"Light Blonde",
"Platinum Blonde",
"Strawberry Blonde",
"Auburn",
"Dark Red",
"Bright Red",
"Copper",
"Dark Gray",
"Silver Gray",
"White",
"Salt and Pepper",
"Bald/Shaved",
"Colored - Blue",
"Colored - Green",
"Colored - Purple",
"Colored - Pink",
"Colored - Other",
"Other"
];

// Detailed eye color options
const eyeColorOptions = [
"Dark Brown",
"Medium Brown",
"Light Brown",
"Amber",
"Hazel",
"Dark Green",
"Light Green",
"Gray-Green",
"Dark Blue",
"Medium Blue",
"Light Blue",
"Blue-Gray",
"Gray",
"Black",
"Heterochromia (Different colored eyes)",
"Other"
];

const educationLevelOptions = [
"High School",
"Some College",
"Associate's Degree",
"Bachelor's Degree",
"Master's Degree",
"Doctorate",
"Trade/Vocational School",
"Prefer not to say"
];

const incomeRangeOptions = [
"Under $25,000",
"$25,000 - $49,999",
"$50,000 - $74,999",
"$75,000 - $99,999",
"$100,000 - $149,999",
"$150,000 - $199,999",
"$200,000+",
"Prefer not to say"
];

const denominationOptions = [
"Baptist",
"Catholic",
"Lutheran",
"Methodist",
"Presbyterian",
"Anglican/Episcopal",
"Pentecostal",
"Non-denominational",
"Evangelical",
"Reformed",
"Orthodox",
"Adventist",
"Christian & Missionary Alliance",
"Assembly of God",
"Charismatic",
"Messianic",
"Other"
];

const attendanceFrequencyOptions = [
"Multiple times per week",
"Once per week",
"A few times per month",
"Once per month",
"Several times per year",
"Rarely"
];

const spiritualMaturityOptions = [
"New believer",
"Growing in faith",
"Established believer",
"Mature believer/Mentor to others"
];

const prayerFrequencyOptions = [
"Multiple times daily",
"Daily",
"Several times per week",
"Weekly",
"Occasionally"
];

const bibleReadingOptions = [
"Daily",
"Several times per week",
"Weekly",
"Occasionally",
"Rarely"
];

const familyPlansOptions = [
"Want children",
"Don't want children",
"Open to children",
"Already have children, want more",
"Already have children, don't want more"
];

const ministryOptions = [
"Worship Team",
"Children's Ministry",
"Youth Ministry",
"Missions",
"Prayer Team",
"Teaching",
"Hospitality",
"Evangelism",
"Media/Tech",
"Counseling",
"Administrative",
"Leadership",
"Discipleship",
"Community Outreach"
];

const hobbyOptions = [
"Reading",
"Hiking",
"Music",
"Cooking",
"Sports",
"Travel",
"Photography",
"Art",
"Volunteering",
"Gardening",
"Fitness",
"Writing",
"Dancing",
"Movies/TV",
"Theater",
"Camping",
"Fishing",
"Hunting",
"Board Games",
"Video Games"
];

const serviceActivitiesOptions = [
"Soup Kitchen/Food Bank",
"Homeless Ministry",
"Prison Ministry",
"Hospital Visitation",
"Elderly Care",
"Disaster Relief",
"Community Clean-up",
"Tutoring/Mentoring",
"International Missions",
"Local Outreach"
];

const languageOptions = [
"English",
"Spanish",
"French",
"German",
"Chinese",
"Arabic",
"Portuguese",
"Russian",
"Japanese",
"Korean",
"Other"
];

// Check if user is authenticated and load their profile
useEffect(() => {
const unsubscribe = onAuthStateChanged(auth, async (user) => {
if (user) {
setUserId(user.uid);

// First, try to get basic user info from auth and users collection
try {
const userDocRef = doc(db, "users", user.uid);
const userDoc = await getDoc(userDocRef);

if (userDoc.exists()) {
setUserInfo({
displayName: user.displayName || userDoc.data().username || "",
photoURL: user.photoURL || userDoc.data().profilePic || "",
email: user.email || userDoc.data().email || ""
});
} else {
setUserInfo({
displayName: user.displayName || "",
photoURL: user.photoURL || "",
email: user.email || ""
});
}
} catch (error) {
console.error("Error fetching user data:", error);
// Fall back to auth data only
setUserInfo({
displayName: user.displayName || "",
photoURL: user.photoURL || "",
email: user.email || ""
});
}

// Now load the full profile data
await loadUserProfile(user.uid);

// Subscribe to messages for notification badge
subscribeToMessages(user.uid);
} else {
// Redirect to login or handle unauthenticated state
setLoadingProfile(false);
console.log("User not authenticated");
}
});

return () => unsubscribe();
}, []);

// Listen for new messages to update notification badge
const subscribeToMessages = (uid) => {
try {
// Listen for chats where the current user is a participant
const chatsQuery = query(
collection(db, "chats"),
where("participants", "array-contains", uid)
);

const unsubscribeChats = onSnapshot(chatsQuery, (chatsSnapshot) => {
let totalUnread = 0;

chatsSnapshot.forEach((chatDoc) => {
const chatData = chatDoc.data();

// Check for unread messages count for current user
if (chatData.unreadCounts && chatData.unreadCounts[uid]) {
totalUnread += chatData.unreadCounts[uid];
}
});

setUnreadMessages(totalUnread);
});

return unsubscribeChats;
} catch (error) {
console.error("Error setting up message listener:", error);
}
};

// Load user profile from Firestore
const loadUserProfile = async (uid) => {
try {
// Try to get profile from profiles collection
const profileDocRef = doc(db, "profiles", uid);
const profileDoc = await getDoc(profileDocRef);

if (profileDoc.exists()) {
// We have a profile, use it
const profileData = profileDoc.data();

// Initialize the form with existing profile data
setFormData(prevData => ({
...prevData,
firstName: profileData.firstName || prevData.firstName,
lastName: profileData.lastName || prevData.lastName,
age: profileData.age || prevData.age,
gender: profileData.gender || prevData.gender,
height: profileData.height || prevData.height,
hairColor: profileData.hairColor || prevData.hairColor,
eyeColor: profileData.eyeColor || prevData.eyeColor,
ethnicity: profileData.ethnicity || prevData.ethnicity,
city: profileData.city || profileData.location || prevData.city,
country: profileData.country || prevData.country,
zipCode: profileData.zipCode || prevData.zipCode,
denomination: profileData.denomination || prevData.denomination,
churchInvolvement: profileData.churchInvolvement || prevData.churchInvolvement,
aboutMe: profileData.aboutMe || prevData.aboutMe,
personalTestimony: profileData.personalTestimony || prevData.personalTestimony,
faithJourney: profileData.faithJourney || prevData.faithJourney,
spiritualMaturity: profileData.spiritualMaturity || prevData.spiritualMaturity,
favoriteBibleVerse: profileData.favoriteBibleVerse || prevData.favoriteBibleVerse,
importantDoctrine: profileData.importantDoctrine || prevData.importantDoctrine,
ministryInterests: profileData.ministryInterests || [],
hobbies: profileData.hobbies || [],
lookingFor: profileData.lookingFor || prevData.lookingFor,
dealBreakers: profileData.dealBreakers || prevData.dealBreakers,
annualIncome: profileData.annualIncome || prevData.annualIncome,
educationLevel: profileData.educationLevel || prevData.educationLevel,
occupation: profileData.occupation || prevData.occupation,
relationshipGoals: profileData.relationshipGoals || prevData.relationshipGoals,
familyPlans: profileData.familyPlans || prevData.familyPlans,
photoUrl: profileData.photoUrl || prevData.photoUrl,
attendanceFrequency: profileData.attendanceFrequency || prevData.attendanceFrequency,
prayerFrequency: profileData.prayerFrequency || prevData.prayerFrequency,
bibleReadingFrequency: profileData.bibleReadingFrequency || prevData.bibleReadingFrequency,
conversionAge: profileData.conversionAge || prevData.conversionAge,
missionTripExperience: profileData.missionTripExperience || prevData.missionTripExperience,
marriageViews: profileData.marriageViews || prevData.marriageViews,
serviceActivities: profileData.serviceActivities || [],
languagesSpoken: profileData.languagesSpoken || []
}));

// Set image preview if there's a photo URL
if (profileData.photoUrl) {
setImagePreview(profileData.photoUrl);
}
} else {
// No profile exists yet, try to get user data from users collection
const userDocRef = doc(db, "users", uid);
const userDoc = await getDoc(userDocRef);

if (userDoc.exists()) {
const userData = userDoc.data();

// Pre-fill form with any available user data
setFormData(prevData => ({
...prevData,
firstName: userData.firstName || userData.username?.split(' ')[0] || '',
lastName: userData.lastName || (userData.username?.split(' ').length > 1 ? userData.username?.split(' ')[1] : '') || '',
photoUrl: userData.profilePic || ''
}));

// Set image preview if there's a profile pic
if (userData.profilePic) {
setImagePreview(userData.profilePic);
}
}
}
} catch (error) {
console.error("Error loading profile:", error);
} finally {
setLoadingProfile(false);
}
};

const handleInputChange = (e) => {
const { name, value } = e.target;
setFormData({
...formData,
[name]: value
});

// Clear error for this field if it exists
if (formErrors[name]) {
setFormErrors({
...formErrors,
[name]: null
});
}
};

const handleCheckboxChange = (type, value) => {
setFormData(prevData => {
const updatedArray = prevData[type]?.includes(value)
? prevData[type].filter(item => item !== value)
: [...(prevData[type] || []), value];

return {
...prevData,
[type]: updatedArray
};
});

// Clear error for this field if it exists
if (formErrors[type]) {
setFormErrors({
...formErrors,
[type]: null
});
}
};

const handleImageChange = (e) => {
if (e.target.files[0]) {
const file = e.target.files[0];

// File size validation (limit to 5MB)
if (file.size > 5 * 1024 * 1024) {
alert('Profile picture must be less than 5MB');
return;
}

// Create image preview
const reader = new FileReader();
reader.onloadend = () => {
setImagePreview(reader.result);
};
reader.readAsDataURL(file);

// Upload the image immediately
uploadImage(file);
}
};

const uploadImage = async (file) => {
if (!userId || !file) return;

setLoading(true);
setUploadProgress(0);

try {
const formData = new FormData();
formData.append('file', file);
formData.append('upload_preset', cloudinaryConfig.uploadPreset);
formData.append('folder', `dating-profiles/${userId}`);

const xhr = new XMLHttpRequest();

// Set up progress tracking
xhr.upload.onprogress = (event) => {
if (event.lengthComputable) {
const percentComplete = Math.round((event.loaded / event.total) * 100);
setUploadProgress(percentComplete);
}
};

xhr.open('POST', cloudinaryConfig.apiUrl, true);

xhr.onload = async function() {
if (xhr.status === 200) {
const response = JSON.parse(xhr.responseText);
const photoUrl = response.secure_url;

// Update formData with the new photoUrl
setFormData(prev => ({
...prev,
photoUrl
}));

// Save the new photoUrl to Firestore
await setDoc(doc(db, "profiles", userId), {
photoUrl
}, { merge: true });

// Also update in users collection
await setDoc(doc(db, "users", userId), {
profilePic: photoUrl
}, { merge: true });

setLoading(false);
setUploadProgress(0);
} else {
console.error('Upload failed with status:', xhr.status);
setLoading(false);
}
};

xhr.onerror = function() {
console.error('Network error during upload');
setLoading(false);
};

xhr.send(formData);
} catch (error) {
console.error("Error uploading image:", error);
setLoading(false);
}
};

// Validate form before submission
const validateForm = () => {
const errors = {};

// Required fields validation
const requiredFields = [
'firstName', 'lastName', 'age', 'gender', 'height', 'ethnicity',
'city', 'country', 'zipCode', 'denomination', 'aboutMe',
'personalTestimony', 'favoriteBibleVerse', 'lookingFor',
'educationLevel', 'occupation', 'relationshipGoals',
'spiritualMaturity', 'attendanceFrequency', 'photoUrl'
];

requiredFields.forEach(field => {
if (!formData[field]) {
errors[field] = 'This field is required';
}
});

// Check minimum length for text fields
const textFields = [
'aboutMe', 'personalTestimony', 'faithJourney',
'lookingFor', 'dealBreakers', 'marriageViews'
];

textFields.forEach(field => {
if (formData[field] && formData[field].length < 50) {
errors[field] = 'Must be at least 50 characters';
}
if (formData[field] && formData[field].length > 4000) {
errors[field] = 'Must be less than 4000 characters';
}
});

// Verify arrays have at least one selection
if (formData.ministryInterests.length === 0) {
errors.ministryInterests = 'Please select at least one ministry interest';
}

if (formData.hobbies.length === 0) {
errors.hobbies = 'Please select at least one hobby or interest';
}

// Age validation
if (formData.age && (isNaN(formData.age) || formData.age < 18 || formData.age > 120)) {
errors.age = 'Age must be between 18 and 120';
}

// Zip code format validation (basic US format)
if (formData.zipCode && !isValidPostalCode(formData.zipCode, formData.country)) {
errors.zipCode = 'Please enter a valid postal/ZIP code for the selected country';
}

return errors;
};

// Helper function to validate postal codes based on country
const isValidPostalCode = (postalCode, country) => {
// Remove spaces for consistent validation
postalCode = postalCode.trim();

// Country-specific postal code formats
switch(country) {
case 'United States':
return /^\d{5}(-\d{4})?$/.test(postalCode); // 12345 or 12345-6789
case 'Canada':
return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postalCode); // A1A 1A1 or A1A-1A1
case 'United Kingdom':
return /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/.test(postalCode); // UK format
case 'Australia':
return /^\d{4}$/.test(postalCode); // 4 digits
case 'Germany':
return /^\d{5}$/.test(postalCode); // 5 digits
case 'France':
return /^\d{5}$/.test(postalCode); // 5 digits
case 'Japan':
return /^\d{3}-\d{4}$/.test(postalCode); // 123-4567
case 'India':
return /^\d{6}$/.test(postalCode); // 6 digits
case 'Brazil':
return /^\d{5}-\d{3}$/.test(postalCode); // 12345-678
// Add more countries as needed

default:
// For countries not explicitly defined, use a more permissive validation
// Most postal codes are 3-10 alphanumeric characters
return /^[A-Za-z0-9 -]{3,10}$/.test(postalCode);
}
};

const saveProfile = async () => {
  setAttemptedSubmit(true);

  // Validate the form
  const errors = validateForm();
  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);

    // Scroll to the first error
    const firstErrorField = document.querySelector('.error-message');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return;
  }

  if (!userId) return;

  setLoading(true);
  try {
    // Prepare the data object once
    const profileData = {
      ...formData,
      updatedAt: new Date()
    };

    // Use Promise.all to save to both collections simultaneously
    await Promise.all([
      setDoc(doc(db, "profiles", userId), profileData),
      formData.photoUrl ? setDoc(doc(db, "users", userId), {
        profilePic: formData.photoUrl
      }, { merge: true }) : Promise.resolve()
    ]);

    setSaved(true);
    setTimeout(() => setSaved(false), 2000); // Reduced from 3000ms to 2000ms
  } catch (error) {
    console.error("Error saving profile:", error);
  } finally {
    setLoading(false);
  }
};

const handleLogout = async () => {
try {
await signOut(auth);
navigate('/signup');
} catch (error) {
console.error("Error signing out:", error);
}
};

// Handle clicking on Messages - mark messages as read when navigating to chat
const handleMessagesClick = async () => {
if (userId && unreadMessages > 0) {
try {
// You can optionally implement immediate clearing of notifications here
// This depends on your Chat.js implementation for how it marks messages as read
console.log("Navigating to messages page");
} catch (error) {
console.error("Error marking messages as read:", error);
}
}
};

// Character counter component for text fields
const CharacterCounter = ({ current, min = 50, max = 4000 }) => {
const count = current ? current.length : 0;
let color = "text-stone-500";

if (count < min) {
color = "text-red-500";
} else if (count > max * 0.9) {
color = "text-amber-600";
}

return (
<div className={`text-xs mt-1 ${color} flex justify-end`}>
{count}/{max} characters {count < min ? `(minimum ${min})` : ''}
</div>
);
};

if (loadingProfile) {
return (
<>
<Header />
<div className="flex justify-center items-center h-screen bg-stone-50">
<Loader className="animate-spin h-10 w-10 text-amber-600" />
<p className="ml-2 text-stone-700">Loading your profile...</p>
</div>
<Footer />
</>
);
}

return (
<>
<Header />

<div className="max-w-4xl mx-auto p-4 bg-stone-50 min-h-screen">
<div className="mb-8">
<h1 className="text-3xl font-bold text-center text-stone-800">Vine Matched Profile</h1>
<p className="text-center text-stone-600 mt-2">Create your detailed profile to connect with other believers</p>
</div>

<div className="bg-white rounded-lg shadow-lg overflow-hidden border border-stone-200">
<div className="flex border-b border-stone-200 flex-wrap">
<button
className={`px-6 py-3 font-medium transition-colors ${activeTab === 'profile' ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-600' : 'text-stone-600 hover:bg-stone-50'}`}
onClick={() => setActiveTab('profile')}
>
Profile
</button>
<button
className={`px-6 py-3 font-medium transition-colors ${activeTab === 'edit' ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-600' : 'text-stone-600 hover:bg-stone-50'}`}
onClick={() => setActiveTab('edit')}
>
Edit Profile
</button>
</div>

{activeTab === 'profile' ? (
<div className="p-6">
<div className="bg-gradient-to-br from-amber-50 to-stone-100 rounded-lg p-8 mb-6 border border-amber-200 shadow-sm">
  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
    <div className="flex-shrink-0 flex justify-center lg:justify-start">
      {formData.photoUrl ? (
        <img
          src={formData.photoUrl}
          alt={`${formData.firstName} ${formData.lastName}`}
          className="w-40 h-40 object-cover rounded-full border-4 border-white shadow-lg"
        />
      ) : (
        <div className="w-40 h-40 bg-stone-200 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
          <User size={56} className="text-stone-400" />
        </div>
      )}
    </div>
    
    <div className="flex-1 space-y-6">
      <div className="text-center lg:text-left">
        <h2 className="text-3xl font-bold text-stone-800 mb-2">
          {formData.firstName} {formData.lastName}
        </h2>
        <div className="flex items-center justify-center lg:justify-start text-amber-600 mb-4">
          <Calendar className="h-5 w-5 mr-2" />
          <span className="text-lg font-medium">{formData.age} years old</span>
        </div>
      </div>

      {/* Location Info */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-200">
        <h4 className="font-semibold text-stone-800 mb-3 text-center lg:text-left">Location & Background</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center text-stone-600">
            <MapPin className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
            <span className="truncate">{formData.city}, {formData.country}</span>
          </div>
          <div className="flex items-center text-stone-600">
            <Users className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
            <span className="truncate">{formData.ethnicity}</span>
          </div>
        </div>
      </div>

      {/* Physical Details */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-200">
        <h4 className="font-semibold text-stone-800 mb-3 text-center lg:text-left">Physical Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="text-center md:text-left">
            <span className="text-sm text-stone-500 block">Height</span>
            <span className="font-medium text-stone-800">{formData.height}</span>
          </div>
          <div className="text-center md:text-left">
            <span className="text-sm text-stone-500 block">Hair</span>
            <span className="font-medium text-stone-800">{formData.hairColor}</span>
          </div>
          <div className="text-center md:text-left">
            <span className="text-sm text-stone-500 block">Eyes</span>
            <span className="font-medium text-stone-800">{formData.eyeColor}</span>
          </div>
        </div>
      </div>

      {/* Faith & Career */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-stone-200">
        <h4 className="font-semibold text-stone-800 mb-3 text-center lg:text-left">Faith & Career</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center text-stone-600">
            <Church className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
            <span className="truncate">{formData.denomination}</span>
          </div>
          <div className="flex items-center text-stone-600">
            <GraduationCap className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
            <span className="truncate">{formData.educationLevel}</span>
          </div>
          <div className="flex items-center text-stone-600 md:col-span-2">
            <Briefcase className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
            <span className="truncate">{formData.occupation}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">About Me</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<p className="text-stone-700">{formData.aboutMe}</p>
</div>
</div>

<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">My Faith Journey</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<p className="text-stone-700">{formData.faithJourney}</p>
</div>
</div>
</div>

<div className="mb-6">
<h3 className="text-lg font-semibold text-stone-800 mb-2">Personal Testimony</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<p className="text-stone-700">{formData.personalTestimony}</p>
</div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
<div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
<h3 className="text-lg font-semibold text-amber-800 flex items-center mb-2">
<Book className="h-5 w-5 mr-2" />
Favorite Bible Verse
</h3>
<p className="text-stone-700 italic">"{formData.favoriteBibleVerse}"</p>
</div>

<div className="bg-stone-100 p-4 rounded-lg border border-stone-300">
<h3 className="text-lg font-semibold text-stone-800 flex items-center mb-2">
<Church className="h-5 w-5 mr-2" />
Church Involvement
</h3>
<p className="text-stone-700">{formData.churchInvolvement}</p>
</div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">Spiritual Life</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<div className="mb-2">
  <span className="font-medium text-amber-700">Spiritual Maturity:</span> {formData.spiritualMaturity}
</div>
<div className="mb-2">
  <span className="font-medium text-amber-700">Church Attendance:</span> {formData.attendanceFrequency}
</div>
<div className="mb-2">
<span className="font-medium text-amber-700">Prayer Frequency:</span> {formData.prayerFrequency}
</div>
<div className="mb-2">
<span className="font-medium text-amber-700">Bible Reading:</span> {formData.bibleReadingFrequency}
</div>
<div className="mb-2">
<span className="font-medium text-amber-700">Age of Conversion:</span> {formData.conversionAge}
</div>
<div>
<span className="font-medium text-amber-700">Mission Experience:</span> {formData.missionTripExperience}
</div>
</div>
</div>

<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">Ministry & Service</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<h4 className="font-medium mb-2">Ministry Interests</h4>
<div className="flex flex-wrap mb-3">
{formData.ministryInterests.map((interest, index) => (
<span key={index} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm mr-2 mb-2 border border-amber-200">
{interest}
</span>
))}
</div>

<h4 className="font-medium mb-2">Service Activities</h4>
<div className="flex flex-wrap">
{formData.serviceActivities.map((activity, index) => (
<span key={index} className="bg-stone-200 text-stone-800 px-3 py-1 rounded-full text-sm mr-2 mb-2">
{activity}
</span>
))}
</div>
</div>
</div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">Relationship Goals</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<div className="mb-2">
<span className="text-lg font-semibold text-amber-700 mb-2">Looking For:</span> {formData.lookingFor}
</div>
<div className="mb-2">
<span className="text-lg font-semibold text-amber-700 mb-2">Relationship Goals:</span> {formData.relationshipGoals}
</div>
<div className="mb-2">
<span className="text-lg font-semibold text-amber-700 mb-2">Family Plans:</span> {formData.familyPlans}
</div>
<div>
<span className="text-lg font-semibold text-amber-700 mb-2">Views on Marriage:</span> {formData.marriageViews}
</div>
</div>
</div>

<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">Deal Breakers</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<p className="text-stone-700">{formData.dealBreakers}</p>
</div>
</div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">Hobbies & Interests</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<div className="flex flex-wrap">
{formData.hobbies.map((hobby, index) => (
<span key={index} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm mr-2 mb-2 border border-amber-200">
{hobby}
</span>
))}
</div>
</div>
</div>

<div>
<h3 className="text-lg font-semibold text-stone-800 mb-2">Languages Spoken</h3>
<div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
<div className="flex flex-wrap">
{formData.languagesSpoken.map((language, index) => (
<span key={index} className="bg-stone-300 text-stone-800 px-3 py-1 rounded-full text-sm mr-2 mb-2">
{language}
</span>
))}
</div>
</div>
</div>
</div>
</div>
) : activeTab === 'edit' ? (
<div className="p-6">
<div className="mb-8">
<div className="flex justify-center">
<div className="relative">
{imagePreview ? (
<img
src={imagePreview}
alt="Profile Preview"
className="w-40 h-40 object-cover rounded-full border-4 border-white shadow-lg"
/>
) : (
<div className="w-40 h-40 bg-stone-200 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
<User size={48} className="text-stone-400" />
</div>
)}

<button
onClick={() => fileInputRef.current.click()}
className="absolute bottom-0 right-0 bg-amber-600 hover:bg-amber-700 text-white rounded-full p-2 shadow-lg transition-colors"
disabled={loading}
>
{loading ? <Loader className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
</button>

<input
type="file"
ref={fileInputRef}
onChange={handleImageChange}
className="hidden"
accept="image/*"
/>
</div>
</div>

{uploadProgress > 0 && uploadProgress < 100 && (
<div className="w-full max-w-xs mx-auto mt-2">
<div className="bg-stone-200 rounded-full h-2">
<div
className="bg-amber-600 h-2 rounded-full transition-all"
style={{width: `${uploadProgress}%`}}
></div>
</div>
<p className="text-xs text-center mt-1 text-stone-600">Uploading: {uploadProgress}%</p>
</div>
)}
</div>

<div className="mb-8">
<h3 className="text-lg font-semibold mb-4 text-stone-800">Basic Information</h3>
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">First Name*</label>
<input
type="text"
name="firstName"
value={formData.firstName}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.firstName && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="First Name"
/>
{formErrors.firstName && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.firstName}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Last Name*</label>
<input
type="text"
name="lastName"
value={formData.lastName}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.lastName && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="Last Name"
/>
{formErrors.lastName && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.lastName}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Age*</label>
<input
type="number"
name="age"
value={formData.age}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.age && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="Your age"
min="18"
/>
{formErrors.age && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.age}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Gender*</label>
<select
name="gender"
value={formData.gender}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.gender && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Gender</option>
<option value="Male">Male</option>
<option value="Female">Female</option>
</select>
{formErrors.gender && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.gender}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Ethnicity*</label>
<select
name="ethnicity"
value={formData.ethnicity}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.ethnicity && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Ethnicity</option>
{ethnicityOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.ethnicity && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.ethnicity}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Height*</label>
<select
name="height"
value={formData.height}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.height && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Height</option>
{heightOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.height && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.height}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Hair Color</label>
<select
name="hairColor"
value={formData.hairColor}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.hairColor && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Hair Color</option>
{hairColorOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.hairColor && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.hairColor}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Eye Color</label>
<select
name="eyeColor"
value={formData.eyeColor}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.eyeColor && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Eye Color</option>
{eyeColorOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.eyeColor && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.eyeColor}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Country*</label>
<input
type="text"
name="country"
value={formData.country}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.country && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="Country"
/>
{formErrors.country && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.country}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">City*</label>
<input
type="text"
name="city"
value={formData.city}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.city && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="City"
/>
{formErrors.city && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.city}</p>
)}
</div>

<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Postal/ZIP Code*</label>
<input
type="text"
name="zipCode"
value={formData.zipCode}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.zipCode && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="Postal/ZIP Code"
/>
{formErrors.zipCode && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.zipCode}</p>
)}
</div>
</div>
</div>
</div>

<div className="mb-8">
<h3 className="text-lg font-semibold mb-4 text-stone-800">Faith & Church</h3>
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Denomination*</label>
<select
name="denomination"
value={formData.denomination}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.denomination && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Denomination</option>
{denominationOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.denomination && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.denomination}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Church Attendance*</label>
<select
name="attendanceFrequency"
value={formData.attendanceFrequency}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.attendanceFrequency && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Attendance Frequency</option>
{attendanceFrequencyOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.attendanceFrequency && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.attendanceFrequency}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Spiritual Maturity*</label>
<select
name="spiritualMaturity"
value={formData.spiritualMaturity}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.spiritualMaturity && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Spiritual Maturity</option>
{spiritualMaturityOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.spiritualMaturity && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.spiritualMaturity}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Prayer Frequency</label>
<select
name="prayerFrequency"
value={formData.prayerFrequency}
onChange={handleInputChange}
className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
>
<option value="">Select Prayer Frequency</option>
{prayerFrequencyOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Bible Reading Frequency</label>
<select
name="bibleReadingFrequency"
value={formData.bibleReadingFrequency}
onChange={handleInputChange}
className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
>
<option value="">Select Bible Reading Frequency</option>
{bibleReadingOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Church Involvement*</label>
<input
type="text"
name="churchInvolvement"
value={formData.churchInvolvement}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.churchInvolvement && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="How are you involved in your church?"
/>
{formErrors.churchInvolvement && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.churchInvolvement}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Age of Conversion</label>
<input
type="text"
name="conversionAge"
value={formData.conversionAge}
onChange={handleInputChange}
className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
placeholder="At what age did you become a Christian?"
/>
</div>
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Favorite Bible Verse*</label>
<input
type="text"
name="favoriteBibleVerse"
value={formData.favoriteBibleVerse}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.favoriteBibleVerse && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="Your favorite Bible verse (reference and text)"
/>
{formErrors.favoriteBibleVerse && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.favoriteBibleVerse}</p>
)}
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Important Doctrine</label>
<input
type="text"
name="importantDoctrine"
value={formData.importantDoctrine}
onChange={handleInputChange}
className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
placeholder="What doctrine/teaching is most important to you?"
/>
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Mission Trip Experience</label>
<input
type="text"
name="missionTripExperience"
value={formData.missionTripExperience}
onChange={handleInputChange}
className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
placeholder="Share your mission trip experience"
/>
</div>
</div>

<div className="mb-8">
<h3 className="text-lg font-semibold mb-4 text-stone-800">About You</h3>
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">About Me* (minimum 50 characters)</label>
<textarea
name="aboutMe"
value={formData.aboutMe}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.aboutMe && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
rows="4"
placeholder="Share about yourself, your personality, interests, and what makes you unique"
></textarea>
{formErrors.aboutMe && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.aboutMe}</p>
)}
<CharacterCounter current={formData.aboutMe} />
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Personal Testimony* (minimum 50 characters)</label>
<textarea
name="personalTestimony"
value={formData.personalTestimony}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.personalTestimony && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
rows="4"
placeholder="Share your testimony - how you came to faith in Christ"
></textarea>
{formErrors.personalTestimony && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.personalTestimony}</p>
)}
<CharacterCounter current={formData.personalTestimony} />
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Faith Journey (minimum 50 characters)</label>
<textarea
name="faithJourney"
value={formData.faithJourney}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.faithJourney && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
rows="4"
placeholder="Describe your spiritual journey and growth"
></textarea>
{formErrors.faithJourney && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.faithJourney}</p>
)}
<CharacterCounter current={formData.faithJourney} />
</div>
</div>

<div className="mb-8">
<h3 className="text-lg font-semibold mb-4 text-stone-800">Ministry & Service</h3>
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Ministry Interests* (select at least one)</label>
<div className="grid grid-cols-2 md:grid-cols-3 gap-2">
{ministryOptions.map((option) => (
<div key={option} className="flex items-center">
<input
type="checkbox"
id={`ministry-${option}`}
checked={formData.ministryInterests.includes(option)}
onChange={() => handleCheckboxChange('ministryInterests', option)}
className="mr-2 accent-amber-600"
/>
<label htmlFor={`ministry-${option}`} className="text-sm text-stone-700">{option}</label>
</div>
))}
</div>
{formErrors.ministryInterests && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.ministryInterests}</p>
)}
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Service Activities (select all that apply)</label>
<div className="grid grid-cols-2 md:grid-cols-3 gap-2">
{serviceActivitiesOptions.map((option) => (
<div key={option} className="flex items-center">
<input
type="checkbox"
id={`service-${option}`}
checked={formData.serviceActivities.includes(option)}
onChange={() => handleCheckboxChange('serviceActivities', option)}
className="mr-2 accent-amber-600"
/>
<label htmlFor={`service-${option}`} className="text-sm text-stone-700">{option}</label>
</div>
))}
</div>
</div>
</div>

<div className="mb-8">
<h3 className="text-lg font-semibold mb-4 text-stone-800">Education & Career</h3>
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Education Level*</label>
<select
name="educationLevel"
value={formData.educationLevel}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.educationLevel && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Education Level</option>
{educationLevelOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
{formErrors.educationLevel && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.educationLevel}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Occupation*</label>
<input
type="text"
name="occupation"
value={formData.occupation}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.occupation && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
placeholder="Your job/occupation"
/>
{formErrors.occupation && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.occupation}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Annual Income</label>
<select
name="annualIncome"
value={formData.annualIncome}
onChange={handleInputChange}
className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
>
<option value="">Select Income Range</option>
{incomeRangeOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
</div>
</div>
</div>

<div className="mb-8">
<h3 className="text-lg font-semibold mb-4 text-stone-800">Relationship Preferences</h3>
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Looking For* (minimum 50 characters)</label>
<textarea
name="lookingFor"
value={formData.lookingFor}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.lookingFor && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
rows="4"
placeholder="Describe the type of person you're hoping to meet"
></textarea>
{formErrors.lookingFor && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.lookingFor}</p>
)}
<CharacterCounter current={formData.lookingFor} />
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Deal Breakers (minimum 50 characters)</label>
<textarea
name="dealBreakers"
value={formData.dealBreakers}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.dealBreakers && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
rows="4"
placeholder="What are your non-negotiables in a relationship?"
></textarea>
{formErrors.dealBreakers && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.dealBreakers}</p>
)}
<CharacterCounter current={formData.dealBreakers} />
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Relationship Goals*</label>
<select
name="relationshipGoals"
value={formData.relationshipGoals}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.relationshipGoals && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
>
<option value="">Select Relationship Goal</option>
<option value="Marriage">Marriage</option>
<option value="Dating leading to marriage">Dating leading to marriage</option>
<option value="Friendship first">Friendship first</option>
<option value="Taking it slow">Taking it slow</option>
<option value="Long-term relationship">Long-term relationship</option>
</select>
{formErrors.relationshipGoals && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.relationshipGoals}</p>
)}
</div>

<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Family Plans</label>
<select
name="familyPlans"
value={formData.familyPlans}
onChange={handleInputChange}
className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
>
<option value="">Select Family Plans</option>
{familyPlansOptions.map((option) => (
<option key={option} value={option}>{option}</option>
))}
</select>
</div>
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Views on Marriage (minimum 50 characters)</label>
<textarea
name="marriageViews"
value={formData.marriageViews}
onChange={handleInputChange}
className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${formErrors.marriageViews && attemptedSubmit ? 'border-red-500' : 'border-stone-300'}`}
rows="4"
placeholder="Share your views on marriage, roles, and expectations"
></textarea>
{formErrors.marriageViews && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.marriageViews}</p>
)}
<CharacterCounter current={formData.marriageViews} />
</div>
</div>

<div className="mb-8">
<h3 className="text-lg font-semibold mb-4 text-stone-800">Hobbies & Interests</h3>
<div>
<label className="block text-stone-700 text-sm font-medium mb-1">Hobbies & Interests* (select at least one)</label>
<div className="grid grid-cols-2 md:grid-cols-4 gap-2">
{hobbyOptions.map((option) => (
<div key={option} className="flex items-center">
<input
type="checkbox"
id={`hobby-${option}`}
checked={formData.hobbies.includes(option)}
onChange={() => handleCheckboxChange('hobbies', option)}
className="mr-2 accent-amber-600"
/>
<label htmlFor={`hobby-${option}`} className="text-sm text-stone-700">{option}</label>
</div>
))}
</div>
{formErrors.hobbies && attemptedSubmit && (
<p className="text-red-500 text-xs mt-1 error-message">{formErrors.hobbies}</p>
)}
</div>

<div className="mt-4">
<label className="block text-stone-700 text-sm font-medium mb-1">Languages Spoken</label>
<div className="grid grid-cols-2 md:grid-cols-4 gap-2">
{languageOptions.map((option) => (
<div key={option} className="flex items-center">
<input
type="checkbox"
id={`language-${option}`}
checked={formData.languagesSpoken.includes(option)}
onChange={() => handleCheckboxChange('languagesSpoken', option)}
className="mr-2 accent-amber-600"
/>
<label htmlFor={`language-${option}`} className="text-sm text-stone-700">{option}</label>
</div>
))}
</div>
</div>
</div>

<div className="flex items-center justify-between mt-8">
<div className="text-stone-600 text-sm">
<span className="text-red-500">*</span> Required fields
</div>
<div className="flex items-center">
{saved && (
<div className="mr-4 text-amber-600 flex items-center">
<span className="mr-1">Profile Saved!</span>
<Star className="h-4 w-4" />
</div>
)}
<button
onClick={saveProfile}
disabled={loading}
className="bg-amber-600 hover:bg-amber-700 text-white font-medium py-3 px-6 rounded-lg flex items-center transition-colors shadow-lg"
>
{loading ? (
<>
<Loader className="animate-spin h-4 w-4 mr-2" />
Saving...
</>
) : (
<>
<Save className="h-4 w-4 mr-2" />
Save Profile
</>
)}
</button>
</div>
</div>
</div>
) : null}
</div>
</div>

<Footer />
</>
);
}